import React, { useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

import { Platform as ReCaptchaPlatform } from 'apollo/queries/autogenerate/schemas-public';
import ChangePhoneEmailOTPIsSent from 'shared/modules/auth/components/organisms/ChangePhoneEmailOTPIsSent';
import ChangePhoneEnterEmail from 'shared/modules/auth/components/organisms/ChangePhoneEnterEmail';
import ChangePhoneEnterEmailOTP from 'shared/modules/auth/components/organisms/ChangePhoneEnterEmailOTP';
import ChangePhoneMobileOTPEntry from 'shared/modules/auth/components/organisms/ChangePhoneMobileOTPEntry';
import ChangePhoneNewMobileOTPSent from 'shared/modules/auth/components/organisms/ChangePhoneNewMobileOTPSent';
import ChangePhoneSuccess from 'shared/modules/auth/components/organisms/ChangePhoneSuccess';
import Dialog from 'shared/modules/common/components/atoms/Dialog';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  /**
   * Invoked when editing is successful.
   * Automatically invoked in 3 seconds after successful update.
   */
  onSuccess?: () => void;
  /**
   * WEB | IOS | ANDROID
   */
  reCaptchaPlatform: ReCaptchaPlatform;
  /**
   *  Every step requires a new ReCaptcha token. This function should return a Promise with the ReCaptcha token as the resolved value.
   */
  getRecaptchaTokenFunction: () => Promise<string>;
  onNavigateBack: () => void;
};

const EditUserPhoneUnauthenticated = (props: Props) => {
  const [stepCounter, setStepCounter] = useState(1);
  const [email, setEmail] = useState('');
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [emailOTP, setEmailOTP] = useState('');

  const cleanUpState = () => {
    setStepCounter(1);

    setEmail('');
    setCurrentPhoneNumber('');
    setNewPhoneNumber('');
  };

  const onClose = () => {
    cleanUpState();
    props.onClose();
  };

  const onNavigateBack = () => {
    cleanUpState();
    props.onNavigateBack();
  };

  const onBackButtonPress = () => {
    if (stepCounter === 1) {
      onNavigateBack();
    } else {
      setStepCounter(stepCounter - 1);
    }
  };

  return (
    <Dialog
      title="携帯電話番号の確認"
      open={props.isVisible}
      showBackButton={stepCounter <= 2}
      onBackButtonPress={onBackButtonPress}
      onCloseButtonPress={onClose}
      style={style.dialog}
    >
      <View
        {...Platform.select({ web: { accessibilityRole: 'form' } })}
        style={style.container}
      >
        {/* STEP 1 - Current Email + Current Phone + New Phone */}
        {stepCounter === 1 && (
          <ChangePhoneEnterEmail
            getRecaptchaTokenFunction={props.getRecaptchaTokenFunction}
            reCaptchaPlatform={ReCaptchaPlatform.WEB}
            onSuccess={(
              userEmail: string,
              userPhone: string,
              userNewPhone: string
            ) => {
              setEmail(userEmail);
              setCurrentPhoneNumber(userPhone);
              setNewPhoneNumber(userNewPhone);
              setStepCounter(2);
            }}
          />
        )}
        {/* STEP 2 - Email OTP Sent Notice */}
        {stepCounter === 2 && (
          <ChangePhoneEmailOTPIsSent
            onSubmit={() => {
              setStepCounter(3);
            }}
          />
        )}
        {/* STEP 3 - Email OTP Entry */}
        {stepCounter === 3 && (
          <ChangePhoneEnterEmailOTP
            getRecaptchaTokenFunction={props.getRecaptchaTokenFunction}
            reCaptchaPlatform={ReCaptchaPlatform.WEB}
            email={email}
            currentPhoneNumber={currentPhoneNumber}
            newPhoneNumber={newPhoneNumber}
            onSuccess={(otp: string) => {
              setEmailOTP(otp);
              setStepCounter(4);
            }}
            onError={(errorType) => {
              if (errorType === 'sendUserBackToBeginning') {
                setStepCounter(1);
              } else if (errorType === 'goBackOneStep') {
                setStepCounter(3);
              }
            }}
          />
        )}
        {/* STEP 4 - New Phone OTP Sent Notification */}
        {stepCounter === 4 && (
          <ChangePhoneNewMobileOTPSent
            newPhoneNumber={newPhoneNumber}
            onNextButtonPress={() => {
              setStepCounter(5);
            }}
          />
        )}
        {/* STEP 5 - New Phone OTP Entry */}
        {stepCounter === 5 && (
          <ChangePhoneMobileOTPEntry
            newPhoneNumber={newPhoneNumber}
            email={email}
            currentPhoneNumber={currentPhoneNumber}
            getRecaptchaTokenFunction={props.getRecaptchaTokenFunction}
            emailOTP={emailOTP}
            onError={() => {
              setStepCounter(1);
            }}
            reCaptchaPlatform={ReCaptchaPlatform.WEB}
            onOTPRequest={() => {
              setStepCounter(4);
            }}
            onSuccess={() => {
              setStepCounter(6);
              setTimeout(() => {
                cleanUpState();
                props.onSuccess?.();
              }, 3000);
            }}
          />
        )}
        {/* STEP 6 - SUCCESS */}
        {stepCounter === 6 && <ChangePhoneSuccess />}
      </View>
    </Dialog>
  );
};

const style = StyleSheet.create({
  container: {
    flex: 1,
  },
  dialog: {
    maxHeight: 684,
  },
});

export default EditUserPhoneUnauthenticated;
